import "@/App.css";
import { useEffect } from "react";
import { canOrder } from "@/lib/cases";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import Home from "@/pages/Home";
import RequestAccess from "@/pages/RequestAccess";
import SignIn from "@/pages/SignIn";
import AuthCallback from "@/pages/AuthCallback";
import AcceptInvite from "@/pages/AcceptInvite";
import ResetPassword from "@/pages/ResetPassword";
import Console from "@/pages/Console";
import AdminInbox from "@/pages/admin/AdminInbox";
import AdminOptIns from "@/pages/admin/AdminOptIns";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminCases from "@/pages/admin/AdminCases";
import AdminContent from "@/pages/admin/AdminContent";
import CasesList from "@/pages/console/CasesList";
import NewCase from "@/pages/console/NewCase";
import CaseDetail from "@/pages/console/CaseDetail";
import Team from "@/pages/console/Team";
import Consent from "@/pages/Consent";
import DisputeReport from "@/pages/DisputeReport";
import VerifyReport from "@/pages/VerifyReport";
import SubjectRights from "@/pages/SubjectRights";
import ComplianceFramework from "@/pages/ComplianceFramework";
import ApiDocs from "@/pages/ApiDocs";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import CaliforniaPrivacy from "@/pages/CaliforniaPrivacy";
import AdminPrivacy from "@/pages/admin/AdminPrivacy";
import AdminMessages from "@/pages/admin/AdminMessages";
import Contact from "@/pages/Contact";
import Pricing from "@/pages/Pricing";
import Blog from "@/pages/Blog";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import Ops from "@/pages/Ops";
import MyAccount from "@/pages/MyAccount";
import FieldVisits from "@/pages/FieldVisits";
import AdminOrgs from "@/pages/admin/AdminOrgs";
import BlogPost from "@/pages/BlogPost";
import PaymentResult from "@/pages/PaymentResult";
import AdminPayments from "@/pages/admin/AdminPayments";
import CommsOptIn from "@/pages/CommsOptIn";
import MessagingTerms from "@/pages/MessagingTerms";
import NotFound from "@/pages/NotFound";
import { SeoManager } from "@/lib/seo";
import HowItWorksBusiness from "@/pages/HowItWorksBusiness";
import HowItWorksPeople from "@/pages/HowItWorksPeople";
import Analytics from "@/pages/console/Analytics";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";

const isSuper = (u) => u.platform_role === "super_admin";
const admin = (el) => <ProtectedRoute allow={isSuper}>{el}</ProtectedRoute>;
const org = (el, allow = (u) => !!u.org_id) => <ProtectedRoute allow={allow}>{el}</ProtectedRoute>;

function AppRouter() {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) window.scrollTo(0, 0);
  }, [location.pathname, location.hash]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/request-access" element={<RequestAccess />} />
      <Route path="/signin" element={<SignIn />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route path="/console" element={<ProtectedRoute allow={(u) => !isSuper(u) && u.account_type !== "consumer"}><Console /></ProtectedRoute>} />
      <Route path="/console/cases" element={org(<CasesList />)} />
      <Route path="/console/cases/new" element={org(<NewCase />, canOrder)} />
      <Route path="/console/cases/:id" element={org(<CaseDetail />)} />
      <Route path="/console/analytics" element={org(<Analytics />, (u) => ["org_admin", "requester"].includes(u.org_role))} />
      <Route path="/admin/analytics" element={admin(<AdminAnalytics />)} />
      <Route path="/console/team" element={org(<Team />, (u) => u.org_role === "org_admin")} />
      <Route path="/consent/:token" element={<Consent />} />
      <Route path="/admin/cases" element={admin(<AdminCases />)} />
      <Route path="/admin/content" element={admin(<AdminContent />)} />
      <Route path="/admin" element={<Navigate to="/admin/inbox" replace />} />
      <Route path="/admin/inbox" element={admin(<AdminInbox />)} />
      <Route path="/admin/opt-ins" element={admin(<AdminOptIns />)} />
      <Route path="/admin/privacy" element={admin(<AdminPrivacy />)} />
      <Route path="/admin/messages" element={admin(<AdminMessages />)} />
      <Route path="/how-it-works/business" element={<HowItWorksBusiness />} />
      <Route path="/how-it-works/individuals" element={<HowItWorksPeople />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/blog" element={<Blog />} />
      <Route path="/signup" element={<Signup kind="business" />} />
      <Route path="/signup/personal" element={<Signup kind="personal" />} />
      <Route path="/verify-email/:token" element={<VerifyEmail />} />
      <Route path="/ops" element={<ProtectedRoute allow={(u) => ["super_admin", "analyst", "qa_reviewer"].includes(u.platform_role)}><Ops /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute allow={(u) => u.account_type === "consumer"}><MyAccount /></ProtectedRoute>} />
      <Route path="/field" element={<ProtectedRoute allow={(u) => u.platform_role === "field_agent"}><FieldVisits /></ProtectedRoute>} />
      <Route path="/admin/orgs" element={admin(<AdminOrgs />)} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      <Route path="/payment/success" element={<PaymentResult />} />
      <Route path="/payment/cancel" element={<PaymentResult />} />
      <Route path="/admin/payments" element={admin(<AdminPayments />)} />
      <Route path="/admin/users" element={admin(<AdminUsers />)} />
      <Route path="/dispute" element={<DisputeReport />} />
      <Route path="/verify" element={<VerifyReport />} />
      <Route path="/subject-rights" element={<SubjectRights />} />
      <Route path="/compliance" element={<ComplianceFramework />} />
      <Route path="/api-docs" element={<ApiDocs />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/california-privacy" element={<CaliforniaPrivacy />} />
      <Route path="/opt-in" element={<CommsOptIn />} />
      <Route path="/messaging-terms" element={<MessagingTerms />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <SeoManager />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
